import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Accessibility, Eye, Keyboard, Monitor, MessageSquare, Settings } from "lucide-react";

const AccessibilityPage = () => {
  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-slate-600 hover:text-slate-800">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-bold text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Accessibility Statement
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
              <Accessibility className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Accessibility Statement</h1>
              <p className="text-slate-500">Making KiddiPlay Club usable for everyone</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <p className="lead text-lg text-slate-600 mb-8">
              KiddiPlay Club is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards.
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Our Commitment</h2>
              <p className="text-slate-600">
                We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards. These guidelines explain how to make web content more accessible to people with disabilities, including:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Visual impairments (blindness, low vision, colour blindness)</li>
                <li>Hearing impairments</li>
                <li>Motor impairments</li>
                <li>Cognitive impairments</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Eye className="w-5 h-5 text-blue-500" />
                Visual Accessibility
              </h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Text can be resized up to 200% without loss of functionality</li>
                <li>Colour contrast meets WCAG AA standards (4.5:1 for normal text)</li>
                <li>We don't rely solely on colour to convey information</li>
                <li>Images have alternative text descriptions</li>
                <li>Compatible with screen readers (VoiceOver, NVDA, JAWS)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Keyboard className="w-5 h-5 text-blue-500" />
                Keyboard Navigation
              </h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>All interactive elements are keyboard accessible</li>
                <li>Focus indicators are visible on all focusable elements</li>
                <li>Tab order follows a logical sequence</li>
                <li>No keyboard traps - you can always navigate away</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Monitor className="w-5 h-5 text-blue-500" />
                Technical Features
              </h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Responsive design works on all screen sizes</li>
                <li>Progressive Web App (PWA) for offline access</li>
                <li>Clear headings and semantic HTML structure</li>
                <li>Form inputs have visible labels</li>
                <li>Error messages are descriptive and helpful</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Settings className="w-5 h-5 text-blue-500" />
                Customisation Tips
              </h2>
              <p className="text-slate-600 mb-4">You can improve your experience using your device's built-in settings:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Increase text size:</strong> Use your browser's zoom function (Ctrl/Cmd + Plus)</li>
                <li><strong>High contrast:</strong> Enable high contrast mode in your operating system settings</li>
                <li><strong>Screen reader:</strong> Enable VoiceOver (Mac/iOS) or TalkBack (Android)</li>
                <li><strong>Reduce motion:</strong> Enable "Reduce Motion" in your device settings</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Known Limitations</h2>
              <p className="text-slate-600 mb-4">
                We are aware of the following accessibility issues and are working to address them:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Some third-party embedded content may not be fully accessible</li>
                <li>Older user-uploaded images may lack alternative text</li>
                <li>Some complex interactive features may have limited screen reader support</li>
              </ul>
            </section>

            <section className="p-6 bg-blue-50 rounded-xl">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <MessageSquare className="w-5 h-5 text-blue-500" />
                Feedback & Contact
              </h2>
              <p className="text-slate-600 mb-4">
                We welcome your feedback on the accessibility of KiddiPlay Club. If you encounter any barriers or have suggestions for improvement:
              </p>
              <p className="text-slate-700">
                <strong>Email:</strong> <a href="mailto:accessibility@kiddiplayclub.com" className="text-blue-600 underline">accessibility@kiddiplayclub.com</a>
              </p>
              <p className="text-slate-600 mt-4 text-sm">
                We aim to respond to accessibility feedback within 5 working days.
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccessibilityPage;
